import { Input } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { PureComponent, ReactNode, TextareaHTMLAttributes } from "react";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { CommonProps } from "../common-props";
import { Icon } from "../icon/icon";

interface TextAreaProps extends TextareaHTMLAttributes<any> {
  value?: string;
  allowClear?: boolean;
  autoSize?: boolean;
  showCount?: boolean;
  formatter: "None" | "Lowercase" | "Uppercase";
  readOnly?: boolean;
  maxLength?: number;
  speechToText?: boolean;
}

interface TextAreaState {
  recognizing: boolean;
}

declare let window: Window & { [key: string]: any };
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

class TextArea extends PureComponent<TextAreaProps & CommonProps, TextAreaState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`ktextarea_${this.uniqueKey}`);

  private recognition: typeof SpeechRecognition | null = null;

  constructor(props: TextAreaProps) {
    super(props);
    this.state = {
      recognizing: false
    };
  }

  componentDidMount() {
    this.setDynamicStyle();

    if (window?.kuika?.isDesignTime || (!("SpeechRecognition" in window) && !("webkitSpeechRecognition" in window))) {
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = false; // Stop after one sentence
    this.recognition.interimResults = false; // Get final results only
    this.recognition.lang = navigator.language;

    this.recognition.onstart = () => this.setState({ recognizing: true });

    this.recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      const value = `${this.props.value ?? ""} ${transcript}`.trim();
      this.props.onChange({ target: { value } } as any);
    };

    this.recognition.onerror = (event) => {
      KMainFunctions.displayWarningNotification(`Speech recognition error: ${event.error}`);
    };

    this.recognition.onend = () => this.setState({ recognizing: false });
  }

  componentDidUpdate(prevProps: TextAreaProps) {
    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  componentWillUnmount() {
    this.style.removeStyle();
  }

  getProps = () => {
    const props: any = _.clone(this.props);
    const style: any = _.clone(this.props.style);

    if (props.formatter === "None") {
      style.textTransform = "none";
      props.value = props.value;
    } else if (props.formatter === "Uppercase") {
      style.textTransform = "uppercase";
      props.value = props.value ? props.value.toString().toLocaleUpperCase("TR") : undefined;
    } else if (props.formatter === "Lowercase") {
      style.textTransform = "lowercase";
      props.value = props.value = props.value ? props.value.toString().toLocaleLowerCase("TR") : undefined;
    }

    if (props?.value?.toString && props.maxLength) {
      props.value = props.value.toString().substring(0, props.maxLength);
    }

    if (props.readOnly) {
      props.readOnly = true;
    } else {
      delete props.readOnly;
    }

    props.style = style;

    return props;
  };

  getClassName = () => {
    let result = "";
    if (this.props.className) {
      result = this.props.className;
    }
    if (!this.uniqueKey) {
      return result;
    }
    result = `${result} ktextarea_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";

    const { showCount } = this.props;
    if (showCount) {
      result += `.${className.trim()} {
        text-align: -webkit-right !important;
      }`;

      result += `.${className.trim()} .ant-input-data-count {
        float: none !important;
      }`;
    }

    if ((this.props.allowClear || this.props.showCount) && this.props.style?.height) {
      result += `.${className.trim()} {
        ${this.props.style?.height && `height: calc(100% - 24px) !important`};
      }`;
      result += `.${className.trim()} textarea.ant-input {
        height: calc(100% - 24px) !important;
      }`;
    }

    if (this.props.speechToText) {
      result += `.${className.trim()} {
        padding-right: calc(${this.props.style.paddingRight}px + 28px) !important;
        padding-right: calc(${this.props.style.paddingTop}px + 28px) !important;
      }`;
    }
    return result;
  };

  handleMicClick = () => {
    if (!this.recognition) return;

    if (this.state.recognizing) {
      this.recognition.stop();
    } else {
      if (!("SpeechRecognition" in window) && !("webkitSpeechRecognition" in window)) {
        KMainFunctions.displayWarningNotification("Speech to Text is not supported in this browser.");

        return;
      }
      this.recognition.start();
    }
  };

  render(): ReactNode {
    return (
      <>
        <Input.TextArea {...this.getProps()} className={this.getClassName()} />
        {this.props.speechToText && (
          <Icon
            iconName="mic"
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              transform: "translate(-50%, -50%)",
              cursor: "pointer",
              color: this.state.recognizing ? "#246dff" : undefined
            }}
            onClick={(e) => {
              e.preventDefault();
              this.handleMicClick();
            }}
          />
        )}
      </>
    );
  }
}

const textArea = withCommonEvents(TextArea);
export { textArea as TextArea };
